// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartFrontSideViewer.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import ReportPartStore from 'logic/Izenda.ReportPartStore';
import Loading from 'share/components/controls/Izenda.Loading';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import reportPartTypeManager from 'Izenda.ReportPartTypeManager';
import SubReportUtils from 'share/utils/Izenda.SubReportUtils';
import IntergrateStore from 'share/utils/Izenda.IntergrateStore';
import izendaCfg from 'Izenda.Config';
import commonDataStore from 'logic/Izenda.ReportDesignerCommonDataStore';
import { get, getOr, isEqual, invoke } from 'lodash/fp';

@observer
export default class ReportPartFrontSideViewer extends Component {
  static propTypes = {
    containerSizeFixed: PropTypes.bool
  };

  static defaultProps = {
    containerSizeFixed: true
  };

  constructor(props: any) {
    super(props);
    this.state = {
      fulfilledData: false
    };
    const { reportPart } = props;
    this.mounted = false;
    // Load custom format here, it's will remove when 20193 implement
    commonDataStore.loadCustomDataFormat();

    this.reportPartStore = new ReportPartStore();
    this.reportPartStore.pageId = props.page;

    if (reportPart) {
      // already have report part provide by parent component
      //  don't need to load report part definition and report part data
      this.reportPartStore.reportPart = props.reportPart;
      this.reportPartStore.readyData = true;
    } else {
      this.reloadData(props).then(() => {
        this.mounted && this.reportPartStore.readyData && this.setState({ fulfilledData: true });
      });
    }
  }

  componentDidMount() {
    const { notInvokeRenderCallBackAfterDidMount, reportPart, printingMode } = this.props;
    if (!notInvokeRenderCallBackAfterDidMount) {
      invoke('afterRenderCallBack', this.props);
    }

    reportPart && izendaCfg.Integration && IntergrateStore.updateReportPart(reportPart);

    if (reportPart && printingMode) {
      this.setState({ fulfilledData: true });
    }
  }

  componentWillReceiveProps(nextProps: any) {
    const { props, reportPartStore } = this;
    const { previewRecords } = props;
    if (
      props.reportPartId !== nextProps.reportPartId ||
      props.reportPartDef !== nextProps.reportPartDef ||
      !isEqual(props.subReportFilter, nextProps.subReportFilter)
    ) {
      this.reloadData(nextProps);
    } else if (
      !props.setting &&
      (!isEqual(props.subReportFilter, nextProps.subReportFilter) ||
        (izendaCfg.Integration && reportPartStore.reportPart))
    ) {
      reportPartStore.updatePreviewData(
        nextProps.subReportFilter,
        reportPartStore.reportPart,
        { filters: nextProps.crosssfilters },
        null,
        nextProps.printingMode,
        true,
        previewRecords
      );
    }
  }

  shouldComponentUpdate(nextProps: any, nextState: any) {
    return !(nextProps.printingMode && this.state.fulfilledData === nextState.fulfilledData);
  }

  componentDidUpdate() {
    izendaCfg.Integration && IntergrateStore.updateReportPart(this.reportPartStore.reportPart);
    invoke('afterRenderCallBack', this.props);
  }

  reloadData(props: any) {
    const {
      reportPartDef,
      printingMode,
      intergrateMode,
      location,
      subReportFilterFromQuery,
      subReportFilter,
      reportPartId,
      reportId,
      previewRecords
    } = props;
    const { reportPartStore } = this;
    const query = intergrateMode ? props.query : get('query', location);

    const draftId = getOr(null, 'draftId', query);
    const reportPartName = getOr(null, 'reportPartName', query);
    const filterPromise = subReportFilterFromQuery
      ? SubReportUtils.resolveSubReportFilter(subReportFilterFromQuery)
      : Promise.resolve(subReportFilter);

    return filterPromise.then(subReportFilter => {
      if (draftId && reportPartName) {
        return reportPartStore.getDraftReportPartDefinition(
          draftId,
          reportPartName,
          subReportFilter,
          printingMode,
          previewRecords
        );
      } else if (reportPartDef) {
        return reportPartStore.updateDefinition(reportPartDef, subReportFilter, printingMode, previewRecords);
      }
      return reportPartStore.getReportPartDefinition(
        reportPartId,
        subReportFilter,
        printingMode,
        reportId,
        previewRecords
      );
    });
  }

  /**
   * Call when the report part completes rendering its content.
   */
  completedRender = () => {
    const { finishedRender, printingMode } = this.props;

    printingMode && typeof evoPdfConverter !== 'undefined' && evoPdfConverter.startConversion();

    typeof finishedRender === 'function' && finishedRender();
  };

  /**
   * Check break page.
   * This is intended to support exporting get part with specific separator value.
   */
  checkBreakPage() {
    const { reportPart } = this.reportPartStore;
    return reportPart.reportPartContent.checkBreakPage && reportPart.reportPartContent.checkBreakPage();
  }

  mountReportPartDom = ref => {
    this.mounted = true;
    this.frontSidePartRef = ref;
  };

  render() {
    const { props } = this;
    const store = this.props.store || this.reportPartStore;
    const { reportPart } = this.reportPartStore;
    const {
      subReportFilter,
      printingMode,
      hideGridPopup,
      embededReportPart,
      containerSizeFixed,
      forExporting,
      width,
      height,
      exportFormatSetting,
      setting,
      afterRenderCallBack,
      isFirstReportPart,
      hideTitle,
      separator,
      publishDrillInfo,
      forDashboard,
      viewMode,
      previewRecords
    } = this.props;
    if (!reportPart) {
      return <Loading show />;
    }

    const FrontSide = reportPartTypeManager.getReportFrontSide(
      reportPart.type,
      props.reportPart && props.store
        ? reportPartTypeManager.DISPLAY_SECTION.Report
        : reportPartTypeManager.DISPLAY_SECTION.Viewer
    );

    const defaultSize = { width: '100%', height: '100%' };
    let containerWidth, containerHeight;

    if (embededReportPart) {
      containerWidth = props.containerWidth;
      containerHeight = props.containerHeight;
    }

    const style = containerSizeFixed ? reportPart.getContainerSize(containerWidth, containerHeight) : defaultSize;
    const containerStyle = !printingMode ? style : defaultSize;

    if (forExporting) {
      containerStyle.width = width;
      containerStyle.height = height;
    }

    const content = (
      <FrontSide
        style={printingMode && !forExporting ? style : defaultSize}
        reportPart={reportPart}
        store={store}
        subReportFilter={subReportFilter}
        afterRenderCallBack={afterRenderCallBack}
        printingMode={printingMode}
        setting={setting}
        exportFormatSetting={exportFormatSetting}
        finishedRender={this.completedRender}
        fulfilledData={this.state.fulfilledData}
        doesBreakPage={this.checkBreakPage()}
        isFirstReportPart={isFirstReportPart}
        hideTitle={hideTitle}
        separator={separator}
        forExporting={forExporting}
        embededReportPart={embededReportPart}
        hideGridPopup={printingMode || hideGridPopup || embededReportPart}
        publishDrillInfo={publishDrillInfo}
        forDashboard={forDashboard}
        viewMode={viewMode}
        frontSidePartRef={this.frontSidePartRef}
        previewRecords={previewRecords}
      />
    );
    return (
      <div style={containerStyle} ref={this.mountReportPartDom} className='izenda-ReportPartFrontSideViewer-container'>
        {printingMode || (props.containerSizeFixed && reportPart.autoSize) ? content : <ScrollBox>{content}</ScrollBox>}
      </div>
    );
  }
}


// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartFrontSideViewer/Izenda.ReportPartFrontSideViewer.js